import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useLang } from "../contexts/LangContext";
import { Eye, EyeOff, Globe } from "lucide-react";

const BG_URL = "https://customer-assets.emergentagent.com/job_tracker-manage/artifacts/iwspjraz_background.jpg";
const LOGO_URL = "https://customer-assets.emergentagent.com/job_tracker-manage/artifacts/y4p41isy_Logo-Dark-Horizontal-Big.svg";

export default function LoginPage() {
    const { login } = useAuth();
    const { t, lang, toggleLang } = useLang();
    const navigate = useNavigate();
    const location = useLocation();

    const isAdminRoute = location.pathname === "/sa/login";

    const [nipt, setNipt] = useState("");
    const [username, setUsername] = useState("");
    const [credential, setCredential] = useState(""); // email for staff
    const [password, setPassword] = useState("");
    const [showPass, setShowPass] = useState(false);
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError("");
        setLoading(true);
        try {
            if (isAdminRoute) {
                await login(credential, password, "staff");
            } else {
                await login(nipt, password, "client", username);
            }
            navigate("/dashboard");
        } catch (err) {
            const detail = err.response?.data?.detail;
            if (typeof detail === "string") setError(detail);
            else setError(t("error"));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div
            className="min-h-screen flex items-center justify-center relative"
            style={{
                backgroundImage: `url(${BG_URL})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
                backgroundRepeat: "no-repeat",
            }}
        >
            <div className="absolute inset-0 bg-black/50" />

            {/* Language toggle */}
            <button
                onClick={toggleLang}
                data-testid="lang-toggle-login"
                className="absolute top-5 right-5 flex items-center gap-1.5 text-xs text-white/70 hover:text-white border border-white/25 hover:border-white/50 rounded-full px-3 py-1.5 z-20 transition-colors backdrop-blur-sm"
            >
                <Globe size={12} />
                {lang === "sq" ? "SQ" : "EN"}
            </button>

            {/* Card */}
            <div className="relative z-10 w-full max-w-[420px] mx-4">
                <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
                    <div className="h-1.5 bg-[#E7212E]" />

                    <div className="px-9 py-9">

                        {/* CLIENT PORTAL */}
                        {!isAdminRoute && (
                            <>
                                {/* Logo - large */}
                                <div className="flex justify-center mb-5">
                                    <img
                                        src={LOGO_URL}
                                        alt="Illyria GPS"
                                        className="h-20 object-contain"
                                        onError={e => { e.target.style.display = "none"; }}
                                    />
                                </div>

                                {/* Title */}
                                <div className="text-center mb-7">
                                    <h1 className="text-2xl font-bold text-slate-900 tracking-tight leading-tight">
                                        Portali i Klientit
                                    </h1>
                                    <p className="text-sm text-slate-500 mt-1.5">
                                        Hyrja me kredencialet e kompanisë suaj
                                    </p>
                                </div>

                                <form onSubmit={handleSubmit} className="space-y-4">
                                    {/* NIPT */}
                                    <input
                                        type="text"
                                        value={nipt}
                                        onChange={e => setNipt(e.target.value)}
                                        required
                                        placeholder="NIPT"
                                        data-testid="login-nipt-input"
                                        className="w-full px-4 py-3 border border-slate-200 rounded-lg text-sm font-semibold text-slate-900 placeholder:text-slate-400 placeholder:font-semibold placeholder:tracking-widest bg-slate-50/60 focus:outline-none focus:ring-2 focus:ring-[#E7212E]/20 focus:border-[#E7212E] focus:bg-white transition-all tracking-wider"
                                    />

                                    {/* Username */}
                                    <input
                                        type="text"
                                        value={username}
                                        onChange={e => setUsername(e.target.value)}
                                        required
                                        placeholder="USERNAME"
                                        data-testid="login-username-input"
                                        autoComplete="username"
                                        className="w-full px-4 py-3 border border-slate-200 rounded-lg text-sm font-semibold text-slate-900 placeholder:text-slate-400 placeholder:font-semibold placeholder:tracking-widest bg-slate-50/60 focus:outline-none focus:ring-2 focus:ring-[#E7212E]/20 focus:border-[#E7212E] focus:bg-white transition-all"
                                    />

                                    {/* Password */}
                                    <div className="relative">
                                        <input
                                            type={showPass ? "text" : "password"}
                                            value={password}
                                            onChange={e => setPassword(e.target.value)}
                                            required
                                            autoComplete="current-password"
                                            placeholder="PASSWORD"
                                            data-testid="login-password-input"
                                            className="w-full px-4 py-3 border border-slate-200 rounded-lg text-sm font-semibold text-slate-900 placeholder:text-slate-400 placeholder:font-semibold placeholder:tracking-widest bg-slate-50/60 focus:outline-none focus:ring-2 focus:ring-[#E7212E]/20 focus:border-[#E7212E] focus:bg-white transition-all pr-11"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowPass(!showPass)}
                                            className="absolute right-3 top-3 text-slate-400 hover:text-slate-600 transition-colors"
                                        >
                                            {showPass ? <EyeOff size={17} /> : <Eye size={17} />}
                                        </button>
                                    </div>

                                    {error && (
                                        <div className="bg-rose-50 border border-rose-200 text-rose-700 text-sm px-4 py-3 rounded-lg font-medium" data-testid="login-error">
                                            {error}
                                        </div>
                                    )}

                                    <button
                                        type="submit"
                                        disabled={loading}
                                        data-testid="login-submit-btn"
                                        className="w-full bg-[#E7212E] hover:bg-[#C91C26] active:bg-[#A8151F] text-white font-semibold py-3 rounded-lg transition-colors disabled:opacity-60 disabled:cursor-not-allowed text-sm tracking-wide mt-1"
                                    >
                                        {loading ? (
                                            <span className="flex items-center justify-center gap-2">
                                                <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                                Duke u identifikuar...
                                            </span>
                                        ) : "Identifikohu"}
                                    </button>
                                </form>
                            </>
                        )}

                        {/* ADMIN / STAFF */}
                        {isAdminRoute && (
                            <>
                                <div className="flex justify-center mb-5">
                                    <img
                                        src={LOGO_URL}
                                        alt="Illyria GPS"
                                        className="h-14 object-contain"
                                        onError={e => { e.target.style.display = "none"; }}
                                    />
                                </div>

                                <div className="text-center mb-6">
                                    <h1 className="text-xl font-bold text-slate-900 tracking-tight">
                                        Admin & Staff
                                    </h1>
                                    <p className="text-sm text-slate-500 mt-1">
                                        Hyrje për stafin dhe administratorët
                                    </p>
                                </div>

                                <form onSubmit={handleSubmit} className="space-y-4">
                                    <div>
                                        <label className="block text-xs font-semibold uppercase tracking-widest text-slate-400 mb-2">
                                            {t("email")}
                                        </label>
                                        <input
                                            type="email"
                                            value={credential}
                                            onChange={e => setCredential(e.target.value)}
                                            required
                                            autoComplete="username"
                                            placeholder="admin@illyriagps.al"
                                            data-testid="login-credential-input"
                                            className="w-full px-4 py-3 border border-slate-200 rounded-lg text-sm font-medium text-slate-900 placeholder-slate-400 bg-slate-50/60 focus:outline-none focus:ring-2 focus:ring-[#E7212E]/20 focus:border-[#E7212E] focus:bg-white transition-all"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-xs font-semibold uppercase tracking-widest text-slate-400 mb-2">
                                            {t("password")}
                                        </label>
                                        <div className="relative">
                                            <input
                                                type={showPass ? "text" : "password"}
                                                value={password}
                                                onChange={e => setPassword(e.target.value)}
                                                required
                                                autoComplete="current-password"
                                                placeholder="••••••••"
                                                data-testid="login-password-input"
                                                className="w-full px-4 py-3 border border-slate-200 rounded-lg text-sm font-medium text-slate-900 placeholder-slate-400 bg-slate-50/60 focus:outline-none focus:ring-2 focus:ring-[#E7212E]/20 focus:border-[#E7212E] focus:bg-white transition-all pr-11"
                                            />
                                            <button
                                                type="button"
                                                onClick={() => setShowPass(!showPass)}
                                                className="absolute right-3 top-3 text-slate-400 hover:text-slate-600 transition-colors"
                                            >
                                                {showPass ? <EyeOff size={17} /> : <Eye size={17} />}
                                            </button>
                                        </div>
                                    </div>

                                    {error && (
                                        <div className="bg-rose-50 border border-rose-200 text-rose-700 text-sm px-4 py-3 rounded-lg font-medium" data-testid="login-error">
                                            {error}
                                        </div>
                                    )}

                                    <button
                                        type="submit"
                                        disabled={loading}
                                        data-testid="login-submit-btn"
                                        className="w-full bg-[#E7212E] hover:bg-[#C91C26] active:bg-[#A8151F] text-white font-semibold py-3 rounded-lg transition-colors disabled:opacity-60 disabled:cursor-not-allowed text-sm tracking-wide mt-1"
                                    >
                                        {loading ? (
                                            <span className="flex items-center justify-center gap-2">
                                                <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                                Duke hyrë...
                                            </span>
                                        ) : "Identifikohu"}
                                    </button>
                                </form>

                                <div className="mt-5 text-center">
                                    <a href="/login" className="text-xs text-slate-400 hover:text-[#E7212E] transition-colors">
                                        Portal Klienti &rarr;
                                    </a>
                                </div>
                            </>
                        )}
                    </div>
                </div>

                <p className="text-center text-white/50 text-xs mt-5 font-medium">
                    &copy; {new Date().getFullYear()} Illyria GeoTechnologies. All rights reserved.
                </p>
            </div>
        </div>
    );
}
